import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';

import {
  GET_AVERAGE_PRODUCT_RATINGS,
  GET_PRODUCT_REVIEWS
} from '../../../../shared/graphql/product-detail-queries';

import { usePrevious } from '../../../../shared/hooks/hooks';

import { ExtendedReviewData } from '../../../../shared/types/graphql-types';

interface UsePDPReviewsProps {
  productCodes?: string;
  rearProductCode?: string;
}

function usePDPReviews(props: UsePDPReviewsProps) {
  const { productCodes, rearProductCode } = props;
  const productCode = productCodes?.[0];
  const [includeVariantProductReviews, setIncludeVariantProductReviews] =
    useState(true);
  const prevProductCode = usePrevious(productCode);
  const [pageNumber, setPageNumber] = useState(0);
  const [reviewsFilterSortQuery, setReviewsFilterSortQuery] = useState('');
  const [productReviews, setProductReviews] = useState<
    Array<ExtendedReviewData>
  >([]);
  const [hasProductLineReviews, setHasProductLineReviews] = useState(false);

  const { data } = useQuery(GET_PRODUCT_REVIEWS, {
    variables: {
      includeVariantProductReviews,
      page: pageNumber,
      pageSize: 5,
      productCodes,
      query: reviewsFilterSortQuery,
      rearProductCode
    }
  });

  const { data: avgRatingsData } = useQuery(GET_AVERAGE_PRODUCT_RATINGS, {
    skip: !productCode,
    variables: {
      productCodes: [
        {
          front: productCode,
          rear: rearProductCode || undefined
        }
      ]
    }
  });

  const { averageRatings } = avgRatingsData?.product?.averageRatings?.[0] || {};
  const { reviewSummary, reviews } = data?.product?.reviewsAggregate || {};
  const { results } = reviews || {};

  useEffect(() => {
    if (prevProductCode !== productCode) {
      setPageNumber(0);
    }
  }, [prevProductCode, productCode]);

  useEffect(() => {
    if (pageNumber > 0) {
      setProductReviews(existingReviews =>
        Array.from(new Set([...existingReviews, ...results]))
      );
    } else {
      setProductReviews(results);
    }

    if (results?.length > 0) {
      setHasProductLineReviews(true);
    }
  }, [results, pageNumber]);

  return {
    averageRatings,
    hasProductLineReviews,
    includeVariantProductReviews,
    productReviews,
    reviewSummary,
    reviews,
    setIncludeVariantProductReviews,
    setPageNumber,
    setReviewsFilterSortQuery
  };
}

export default usePDPReviews;
